/* eslint-disable no-console */
import { IStorageService } from './types';

export class QnAConfigStorageService implements IStorageService {
  private readonly prefix: string = 'qna-widget-';
  private readonly fallbackStorage: Map<string, string> = new Map();

  constructor() {
    this.validateStorageAvailability();
  }

  async get(key: string): Promise<string | null> {
    try {
      const fullKey = this.getFullKey(key);

      if (this.isLocalStorageAvailable()) {
        const encodedValue = localStorage.getItem(fullKey);
        return encodedValue ? this.decode(encodedValue) : null;
      }

      return this.fallbackStorage.get(fullKey) || null;
    } catch (error) {
      console.warn('StorageService: Failed to get value', { key, error });
      return null;
    }
  }

  async set(key: string, value: string): Promise<boolean> {
    try {
      const fullKey = this.getFullKey(key);
      const encodedValue = this.encode(value);

      if (this.isLocalStorageAvailable()) {
        localStorage.setItem(fullKey, encodedValue);
      } else {
        this.fallbackStorage.set(fullKey, encodedValue);
      }

      return true;
    } catch (error) {
      console.error('StorageService: Failed to set value', { key, error });
      return false;
    }
  }

  async remove(key: string): Promise<boolean> {
    try {
      const fullKey = this.getFullKey(key);

      if (this.isLocalStorageAvailable()) {
        localStorage.removeItem(fullKey);
      } else {
        this.fallbackStorage.delete(fullKey);
      }

      return true;
    } catch (error) {
      console.error('StorageService: Failed to remove value', { key, error });
      return false;
    }
  }

  async clear(): Promise<boolean> {
    try {
      if (this.isLocalStorageAvailable()) {
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key?.startsWith(this.prefix)) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach((key) => localStorage.removeItem(key));
      } else {
        const keysToRemove = Array.from(this.fallbackStorage.keys())
          .filter((key) => key.startsWith(this.prefix));
        keysToRemove.forEach((key) => this.fallbackStorage.delete(key));
      }

      return true;
    } catch (error) {
      console.error('StorageService: Failed to clear storage', error);
      return false;
    }
  }

  private getFullKey(key: string): string {
    return `${this.prefix}${key}`;
  }

  private encode(value: string): string {
    try {
      return btoa(encodeURIComponent(value));
    } catch (error) {
      console.warn('StorageService: Failed to encode value, using plain text', error);
      return value;
    }
  }

  private decode(encodedValue: string): string {
    try {
      return decodeURIComponent(atob(encodedValue));
    } catch (error) {
      console.warn('StorageService: Failed to decode value, using as-is', error);
      return encodedValue;
    }
  }

  private isLocalStorageAvailable(): boolean {
    try {
      const testKey = '__storage_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }

  private validateStorageAvailability(): void {
    if (!this.isLocalStorageAvailable()) {
      console.warn('StorageService: localStorage is not available, using fallback memory storage');
    }
  }

  getStorageInfo(): { type: 'localStorage' | 'memory'; available: boolean } {
    const isLocalStorageAvailable = this.isLocalStorageAvailable();
    return {
      type: isLocalStorageAvailable ? 'localStorage' : 'memory',
      available: isLocalStorageAvailable,
    };
  }
}
